<template>
  <div>
    <ExSelectPicker
      :pickerLabel="pickerLabel"
      :label="label"
      :searchLabel="searchLabel"
      :items="processedCountries"
      :value="resolvedValue"
      :useCode="useCode"
      :isSelectedItemRemove="isSelectedItemRemove"
      @item-selected="handleItemSelected"
      @clear-selection="clearItemSelected"
      :isforceReadonly="isforceReadonly"
      :isShowDefautValue="isShowDefautValue"
      noRecordMessage="No country found"
      noSearchResultsMessage="No countries found"
      :showLockIcon="true"
      :requireClearToChange="true"
      :alwaysShowClearIcon="true"
      @input-cleared="clearItemSelected"
      :useBackendSearch="useScrollPagination"
      :enablePagination="useScrollPagination"
      :hasMoreData="hasMoreCountries"
      :isLoadingMore="isLoadingMoreCountries"
      :isInitialLoading="isInitialCountryLoad"
      @search="onCountrySearchInput"
      @load-more="loadMoreCountries"
      @list-visibility="onListVisibility"
    />
  </div>
</template>

<script setup>
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import {
  defineProps,
  defineEmits,
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  getCurrentInstance,
} from "vue";
import { resolveCountryFlagIcon } from "@components/utils/countryFlagIcon";
import { getOperatingCountry } from "@/api";

const COUNTRY_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const app = getCurrentInstance();
const { proxy } = app;

const props = defineProps({
  /** Vue 2 v-model (`value` / `input`). */
  value: {
    type: Object,
    required: false,
    default: null,
  },
  /** Vue 3-style / explicit binding. */
  modelValue: {
    type: Object,
    required: false,
    default: null,
  },
  label: {
    type: String,
    required: false,
    default: "Type to search",
  },
  searchLabel: {
    type: String,
    required: false,
    default: "Search country",
  },
  pickerLabel: {
    type: String,
    required: false,
    default: "Country",
  },
  useCode: {
    type: Boolean,
    required: false,
    default: true,
  },
  isSelectedItemRemove: {
    type: Boolean,
    required: false,
    default: true,
  },
  isforceReadonly: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    required: false,
    default: () => [],
  },
  pickerPopUp: {
    type: Boolean,
    required: false,
    default: false,
  },
  isShowDefautValue: {
    type: Boolean,
    required: false,
    default: false,
  },
  /** When true and `items` is empty, show built-in demo countries (legacy). */
  useStaticFallback: {
    type: Boolean,
    required: false,
    default: true,
  },
  /**
   * `static` → demo / items prop (legacy).
   * `operating` → GET /api/common/v2/account/<id>/operating-country/ with scroll pagination.
   */
  listSource: {
    type: String,
    default: "static",
    validator: (value) => ["static", "operating"].includes(value),
  },
});

const emit = defineEmits([
  "input",
  "update:modelValue",
  "item-country-selected",
  "clear-selection",
  "list-visibility",
]);

/** Prefer a real selection from Vue 2 `value` or Vue 3 `modelValue`; otherwise null. */
const resolvedValue = computed(() => {
  const fromValue = props.value;
  const fromModel = props.modelValue;
  if (fromValue != null && fromValue.id != null) return fromValue;
  if (fromModel != null && fromModel.id != null) return fromModel;
  return null;
});

const defaultCountries = [
  { id: 1, name: "United States", icon: "https://flagcdn.com/us.svg" },
  { id: 2, name: "Europe", icon: "https://flagcdn.com/eu.svg" },
  { id: 3, name: "United Kingdom", icon: "https://flagcdn.com/gb.svg" },
  { id: 4, name: "Japan", icon: "https://flagcdn.com/jp.svg" },
  { id: 5, name: "Canada", icon: "https://flagcdn.com/ca.svg" },
  { id: 6, name: "Australia", icon: "https://flagcdn.com/au.svg" },
];

const fetchedCountries = ref([]);
const countryListOffset = ref(0);
const countryTotalCount = ref(0);
const isLoadingMoreCountries = ref(false);
const isInitialCountryLoad = ref(false);
const lastCountryFetchSize = ref(0);
const countryPaginationExhausted = ref(false);
const activeCountrySearch = ref("");
let countrySearchDebounceTimer = null;
let countryFetchGeneration = 0;

const useOperatingList = computed(() => props.listSource === "operating");
const useScrollPagination = computed(
  () => useOperatingList.value && props.items.length === 0
);

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

/** Map operating-country list row → ExSelectPicker item { id, name, icon, code }. */
function mapOperatingCountryRow(row) {
  const country = row?.country || {};
  const code = country.code || row.code || "";
  const icon = resolveCountryFlagIcon({ code, flag_icon: country.flag_icon });
  return {
    id: row.id,
    name: country.name || row.name || "",
    code,
    icon,
    currency: country.currency || null,
    active: row.active !== false,
  };
}

const hasMoreCountries = computed(() => {
  if (!useScrollPagination.value) return false;
  if (countryPaginationExhausted.value) return false;
  if (isInitialCountryLoad.value) return false;
  if (typeof countryTotalCount.value === "number" && countryTotalCount.value > 0) {
    return countryListOffset.value < countryTotalCount.value;
  }
  if (fetchedCountries.value.length === 0) return false;
  return lastCountryFetchSize.value === COUNTRY_PAGE_SIZE;
});

const processedCountries = computed(() => {
  if (props.items.length > 0) {
    return props.items;
  }
  if (useOperatingList.value) {
    return fetchedCountries.value;
  }
  return props.useStaticFallback ? defaultCountries : [];
});

const mergePages = (existing, incoming) => {
  const byId = new Map();
  for (const c of existing) {
    if (c && c.id != null) byId.set(String(c.id), c);
  }
  for (const c of incoming) {
    if (c && c.id != null) byId.set(String(c.id), c);
  }
  return Array.from(byId.values());
};

const fetchCountries = async (append = false) => {
  if (!useScrollPagination.value) return;
  let showGlobalLoader = false;
  if (append) {
    if (isLoadingMoreCountries.value || !hasMoreCountries.value) return;
    isLoadingMoreCountries.value = true;
  } else {
    isLoadingMoreCountries.value = false;
    isInitialCountryLoad.value = true;
    countryPaginationExhausted.value = false;
    countryListOffset.value = 0;
    showGlobalLoader = !props.pickerPopUp && fetchedCountries.value.length === 0;
    if (showGlobalLoader) {
      proxy?.$loader?.show?.();
    }
  }
  const requestOffset = append ? countryListOffset.value : 0;
  const generation = append ? countryFetchGeneration : ++countryFetchGeneration;
  try {
    const q = activeCountrySearch.value.trim();
    const response = await getOperatingCountry(getAccountId(), {
      offset: requestOffset,
      size: COUNTRY_PAGE_SIZE,
      limit: COUNTRY_PAGE_SIZE,
      search: q || undefined,
      status: ["active"],
    });
    if (generation !== countryFetchGeneration) return;

    const raw = response?.data?.results ?? [];
    lastCountryFetchSize.value = raw.length;
    const count = typeof response?.data?.count === "number" ? response.data.count : null;
    if (count != null) {
      countryTotalCount.value = count;
    }
    const batch = raw
      .map(mapOperatingCountryRow)
      .filter((c) => c && c.active !== false && c.name);
    const nextOffset = requestOffset + raw.length;
    if (append) {
      fetchedCountries.value = mergePages(fetchedCountries.value, batch);
      countryListOffset.value = nextOffset;
      if (raw.length === 0) {
        countryPaginationExhausted.value = true;
      }
    } else {
      fetchedCountries.value = batch;
      countryListOffset.value = nextOffset;
      if (count == null) {
        countryTotalCount.value = 0;
      }
    }
  } catch {
    if (generation !== countryFetchGeneration) return;
    if (!append) {
      fetchedCountries.value = [];
      countryTotalCount.value = 0;
    }
    if (append) {
      countryPaginationExhausted.value = true;
    }
  } finally {
    if (append) {
      isLoadingMoreCountries.value = false;
    } else if (generation === countryFetchGeneration) {
      isInitialCountryLoad.value = false;
      if (showGlobalLoader) {
        proxy?.$loader?.hide?.();
      }
    }
  }
};

const loadMoreCountries = () => {
  if (!useScrollPagination.value || isLoadingMoreCountries.value || !hasMoreCountries.value) {
    return;
  }
  fetchCountries(true);
};

const onCountrySearchInput = (raw) => {
  if (!useScrollPagination.value) return;
  const next = typeof raw === "string" ? raw.trim() : raw != null ? String(raw).trim() : "";
  if (countrySearchDebounceTimer) {
    clearTimeout(countrySearchDebounceTimer);
  }
  countrySearchDebounceTimer = setTimeout(() => {
    countrySearchDebounceTimer = null;
    activeCountrySearch.value = next;
    fetchCountries(false);
  }, SEARCH_DEBOUNCE_MS);
};

const syncPickerValue = (selectedItem) => {
  emit("input", selectedItem);
  emit("update:modelValue", selectedItem);
  emit("item-country-selected", selectedItem);
};

const handleItemSelected = (selectedItem) => {
  syncPickerValue(selectedItem);
};

const clearItemSelected = () => {
  emit("input", null);
  emit("update:modelValue", null);
  if (props.pickerPopUp) {
    emit("item-country-selected", null);
  }
  emit("clear-selection");
};

const onListVisibility = (isVisible) => {
  emit("list-visibility", isVisible);
};

onMounted(() => {
  if (useScrollPagination.value) {
    fetchCountries(false);
  }
});

onBeforeUnmount(() => {
  if (countrySearchDebounceTimer) {
    clearTimeout(countrySearchDebounceTimer);
    countrySearchDebounceTimer = null;
  }
});
</script>

<style scoped>
::v-deep(.custom-response-picker) {
  width: 100%;
}
</style>
